import { Injectable } from '@angular/core';
import {
  GridApi,
  ColumnApi,
  CellPosition,
  Column,
  NavigateToNextCellParams,
  TabToNextCellParams,
  SuppressKeyboardEventParams,
  RowNode,
  GridOptions,
  CellFocusedEvent,
  CellMouseDownEvent,
} from 'ag-grid-community';
import {
  findIndex,
  get,
  gt,
  head,
  isEmpty,
  isEqual,
  isFunction,
  isNil,
  last,
  lt,
  merge,
  size,
} from 'lodash';

import {
  IAgGridNavigationOptions,
  INavigationContext,
  IEditableColumn,
  NavigationVerticalDirection,
  NavigationHorizontalDirection,
} from './keyboard-navigation.types';

import { DEFAULT_NAVIGATION_OPTIONS } from './keyboard-navigation.config';

import { NavigationUtils } from './keyboard-navigation.utils';
import { KEY_CODES } from '@config/global-constants';
import {
  clearFocusedCellRenderer,
  getAllRows,
  getCellRendererInstance,
} from '@app/components/xvi-grid/custom-components/cell-renderer-components/renderer-helpers';
import { AgGridService } from '@app/shared/services/ag-grid/ag-grid.service';
import { BaseInjectService } from '@app/shared/services';
import { lazyInject } from '@app/containers/base/decorators';

/**
 * ag-grid keyboard navigation service
 * Provides reusable keyboard navigation functionality
 */
@Injectable()
export class AgGridKeyboardNavigationService extends BaseInjectService {
  @lazyInject(AgGridService)
  protected agGridService: AgGridService;

  private defaultOptions: IAgGridNavigationOptions = DEFAULT_NAVIGATION_OPTIONS;

  /**
   * Creates navigation handlers
   *
   * @param gridApi GridApi instance
   * @param columnApi ColumnApi instance
   * @param options Navigation options
   * @returns Collection of navigation handler functions
   */
  public createNavigationHandlers(
    gridApi: GridApi,
    columnApi: ColumnApi,
    options: Partial<IAgGridNavigationOptions> = {},
  ): Pick<
    GridOptions,
    | 'navigateToNextCell'
    | 'tabToNextCell'
    | 'suppressKeyboardEvent'
    | 'onCellEditingStopped'
    | 'onCellFocused'
    | 'onCellMouseDown'
  > {
    const mergedOptions = merge({}, this.defaultOptions, options);

    return {
      /**
       * navigation handler for arrow keys
       */
      navigateToNextCell: (params: NavigateToNextCellParams): CellPosition => {
        return this.handleNavigateToNextCell(
          gridApi,
          columnApi,
          params,
          mergedOptions,
        );
      },

      /**
       * navigation handler for Tab key
       */
      tabToNextCell: (params: TabToNextCellParams): CellPosition => {
        return this.handleTabToNextCell(
          gridApi,
          columnApi,
          params,
          mergedOptions,
        );
      },

      /**
       * navigation handler for keyboard event suppression
       */
      suppressKeyboardEvent: (params: SuppressKeyboardEventParams): boolean => {
        return this.handleSuppressKeyboardEvent(params, mergedOptions);
      },

      onCellFocused: (event: CellFocusedEvent): void => {
        this.handleOnCellFocused(event, gridApi, mergedOptions);
      },

      onCellMouseDown: (event: CellMouseDownEvent): void => {
        this.handleOnCellMouseDown(event, gridApi, mergedOptions);
      },
    };
  }

  public createNavigationContext(params: {
    gridApi: GridApi;
    columnApi: ColumnApi;
    options: IAgGridNavigationOptions;

    focusedCell?: CellPosition;
  }): INavigationContext {
    const { gridApi, columnApi, options, focusedCell } = params;

    return {
      gridApi,
      columnApi,
      options,
      focusedCell: isNil(focusedCell) ? gridApi.getFocusedCell() : focusedCell,
    };
  }

  /**
   * Handles navigation for arrow keys
   */
  private handleNavigateToNextCell(
    gridApi: GridApi,
    columnApi: ColumnApi,
    params: NavigateToNextCellParams,
    options: IAgGridNavigationOptions,
  ): CellPosition {
    try {
      const focusedCell = gridApi.getFocusedCell();

      // Boundary check
      if (!NavigationUtils.isValidCellPosition(params.nextCellPosition)) {
        return focusedCell;
      }

      if (!isNil(params.nextCellPosition?.rowPinned)) {
        return focusedCell;
      }

      const context: INavigationContext = this.createNavigationContext({
        gridApi,
        columnApi,
        options,
        focusedCell,
      });

      // Vertical navigation
      if (
        [KEY_CODES.ARROW_UP, KEY_CODES.ARROW_DOWN].includes(params.event.key)
      ) {
        return this.navigateVertically({
          currentPosition: params.nextCellPosition,
          direction:
            params.event.key === KEY_CODES.ARROW_UP
              ? NavigationVerticalDirection.UP
              : NavigationVerticalDirection.DOWN,
          context,
        });
      }

      // Check if the next cell is editable
      if (
        NavigationUtils.isEditableCell(
          params.nextCellPosition.column,
          params.nextCellPosition.rowIndex,
          gridApi,
          options,
        )
      ) {
        return params.nextCellPosition;
      }

      // Horizontal navigation
      const direction =
        params.event.key === KEY_CODES.ARROW_LEFT
          ? NavigationHorizontalDirection.LEFT
          : NavigationHorizontalDirection.RIGHT;

      return this.navigateHorizontally({
        direction,
        context,
      });
    } catch (error) {
      console.error(
        '[AgGridKeyboardNavigationService] Navigation error:',
        error,
      );

      return params.nextCellPosition || gridApi.getFocusedCell();
    }
  }

  /**
   * Handles navigation for Tab key
   */
  private handleTabToNextCell(
    gridApi: GridApi,
    columnApi: ColumnApi,
    params: TabToNextCellParams,
    options: IAgGridNavigationOptions,
  ): CellPosition {
    // Boundary check
    if (!NavigationUtils.isValidCellPosition(params.nextCellPosition)) {
      // should return current focused cell position if next cell position is out of boundary to avoid losing focus
      return gridApi.getFocusedCell();
    }

    // Check if the next cell is editable
    if (
      NavigationUtils.isEditableCell(
        params.nextCellPosition.column,
        params.nextCellPosition.rowIndex,
        gridApi,
        options,
      )
    ) {
      return params.nextCellPosition;
    }

    const context: INavigationContext = this.createNavigationContext({
      gridApi,
      columnApi,
      options,
    });

    // Tab key cross-row search for editable cells
    return this.findNextEditableCellByRow({
      nextPosition: params.nextCellPosition,
      backwards: params.backwards,
      context,
    });
  }

  /**
   * Vertical navigation (up and down)
   */
  public navigateVertically(params: {
    currentPosition: CellPosition;
    direction: NavigationVerticalDirection;
    context: INavigationContext;
  }): CellPosition {
    const { currentPosition, direction, context } = params;
    const { gridApi, options } = context;
    const rowCount = gridApi.getModel().getRowCount();

    // Fast path: non-tree mode and no need to find editable cells
    if (!options.alwaysFindNextEditCell && !options.treeViewMode) {
      const nextIndex = NavigationUtils.getNextRowIndex(
        currentPosition.rowIndex,
        direction,
        rowCount,
      );

      if (nextIndex === null) {
        return currentPosition;
      }

      return NavigationUtils.createCellPosition(
        nextIndex,
        currentPosition.column,
        currentPosition.rowPinned,
      );
    }

    // Find the next available row
    return this.findNextAvailableRow({
      currentPosition,
      direction,
      context,
    });
  }

  /**
   * Horizontal navigation (left and right)
   */
  private navigateHorizontally(params: {
    direction: NavigationHorizontalDirection;
    context: INavigationContext;
  }): CellPosition {
    const { direction, context } = params;
    const { columnApi, gridApi, focusedCell, options } = context;

    const allColumns = columnApi.getAllColumns();
    const editableColumns = this.getEditableColumnsForRow(
      focusedCell.rowIndex,
      allColumns,
      gridApi,
      options,
    );

    // should return current position if no editable columns or only current column is editable
    if (
      isEmpty(editableColumns) ||
      (isEqual(size(editableColumns), 1) &&
        isEqual(head(editableColumns).column, focusedCell.column))
    ) {
      return focusedCell;
    }

    const currentIdx = editableColumns.findIndex(
      (col) => col.column.getColId() === focusedCell.column.getColId(),
    );

    const nextIdx = NavigationUtils.getNextColumnIndex(
      currentIdx,
      direction,
      size(editableColumns),
    );

    if (nextIdx === null) {
      return focusedCell;
    }

    const nextColumn = editableColumns[nextIdx].column;
    const newPosition = NavigationUtils.createCellPosition(
      focusedCell.rowIndex,
      nextColumn,
      focusedCell.rowPinned,
    );

    return newPosition;
  }

  /**
   * Find the next available row
   */
  private findNextAvailableRow(params: {
    currentPosition: CellPosition;
    direction: NavigationVerticalDirection;
    context: INavigationContext;
  }): CellPosition {
    const { currentPosition, direction, context } = params;

    const isArrowUp = isEqual(direction, NavigationVerticalDirection.UP);

    const { gridApi, options, focusedCell } = context;
    const allRows: RowNode[] = getAllRows(gridApi, {
      withFilterAndSort: true,
      valueSelector: (row) => row,
    });

    const startIdx = currentPosition.rowIndex;
    const endIdx = isArrowUp ? 0 : allRows.length - 1;
    const step = isArrowUp ? -1 : 1;

    const startFromCurrent = !isEqual(currentPosition, focusedCell);

    // check current position first when start from current( for saving performance )
    if (
      startFromCurrent &&
      (NavigationUtils.isEditableCell(
        currentPosition.column,
        currentPosition.rowIndex,
        gridApi,
        options,
      ) ||
        !NavigationUtils.isEditableColumn(currentPosition.column))
    ) {
      return currentPosition;
    }

    for (
      let rowIndex = startIdx + step;
      isArrowUp ? rowIndex >= endIdx : rowIndex <= endIdx;
      rowIndex += step
    ) {
      const row = allRows[rowIndex];
      const rowData = row?.data;

      const isCellEditable = NavigationUtils.isEditableCell(
        currentPosition.column,
        rowIndex,
        gridApi,
        options,
      );

      if (
        (rowData && isCellEditable) ||
        !NavigationUtils.isEditableColumn(currentPosition.column)
      ) {
        return NavigationUtils.createCellPosition(
          rowIndex,
          currentPosition.column,
          currentPosition.rowPinned,
        );
      }
    }

    // the current position maybe the next position(not editable), so we need to check it before return.
    // if not, just return focusedCell(the last valid position)
    return NavigationUtils.isEditableCell(
      currentPosition.column,
      currentPosition.rowIndex,
      gridApi,
      options,
    )
      ? currentPosition
      : focusedCell;
  }

  private findCellInTheNextRow(params: {
    nextPosition: CellPosition;
    backwards: boolean;
    context: INavigationContext;
  }): CellPosition {
    const { nextPosition, backwards, context } = params;
    const { gridApi, columnApi, options, focusedCell } = context;

    const focusedRowIndex = focusedCell.rowIndex;

    const rowCount = gridApi.getModel().getRowCount();
    const allColumns = columnApi.getAllDisplayedColumns();

    // Check boundary conditions first to avoid unnecessary calculations
    const reachTheBoundary =
      (backwards && focusedRowIndex === 0) ||
      (!backwards && focusedRowIndex === rowCount - 1);

    if (reachTheBoundary) {
      return focusedCell; // Reached the boundary just stop here
    }

    const nextRowIndex = backwards ? focusedRowIndex - 1 : focusedRowIndex + 1;

    // get real next row editable columns to find next row start column
    const rowEditableColumns = this.getEditableColumnsForRow(
      nextRowIndex,
      allColumns,
      gridApi,
      options,
    );

    // Recursive search for the next row
    return this.findNextEditableCellByRow({
      nextPosition: NavigationUtils.createCellPosition(
        nextRowIndex,
        get(rowEditableColumns, [
          backwards ? size(rowEditableColumns) - 1 : 0,
          'column',
        ]),
        nextPosition.rowPinned,
      ),
      backwards,
      context,
    });
  }

  /**
   * Tab key recursive search for editable cells
   */
  private findNextEditableCellByRow(params: {
    nextPosition: CellPosition;
    backwards: boolean;
    context: INavigationContext;
  }): CellPosition {
    const { nextPosition, backwards, context } = params;
    const { gridApi, columnApi, focusedCell, options } = context;

    // If the next cell is editable, return it
    if (
      NavigationUtils.isEditableCell(
        nextPosition.column,
        nextPosition.rowIndex,
        gridApi,
        options,
      )
    ) {
      return nextPosition;
    }

    const allColumns = columnApi.getAllDisplayedColumns();

    // Search in the row of the next position
    const editableColumns = this.getEditableColumnsForRow(
      nextPosition.rowIndex,
      allColumns,
      gridApi,
      options,
    );

    if (isEmpty(editableColumns)) {
      return focusedCell;
    }

    const nextColIdx = editableColumns.findIndex(
      (col) => col.column.getColId() === nextPosition.column.getColId(),
    );

    let editableColIndex = backwards ? nextColIdx - 1 : nextColIdx + 1;

    // non editable for next position
    if (lt(nextColIdx, 0)) {
      const nextPositionColIdxInRow = allColumns.findIndex(
        (col) => col.getColId() === nextPosition.column.getColId(),
      );

      const firstEditableColIdxInRow =
        head(editableColumns)?.indexInRow ??
        allColumns.findIndex(
          (col) => col.getColId() === head(editableColumns).column.getColId(),
        );
      const lastEditableColIdxInRow =
        last(editableColumns)?.indexInRow ??
        allColumns.findIndex(
          (col) => col.getColId() === last(editableColumns).column.getColId(),
        );

      // Move to the next row when the next position column is not editable and out of the editable columns range in the current row
      if (
        backwards
          ? nextPositionColIdxInRow < firstEditableColIdxInRow
          : nextPositionColIdxInRow > lastEditableColIdxInRow
      ) {
        // Move to the next row when is not found in the current row
        return this.findCellInTheNextRow({
          nextPosition,
          backwards,
          context,
        });
      } else {
        const focusedColIndexInRow = allColumns.findIndex(
          (col) => col.getColId() === focusedCell.column.getColId(),
        );

        // find the next editable column index in the current row based on the focused cell position in the row when the next position column is not editable but still in the editable columns range in the current row
        editableColIndex = findIndex(
          backwards ? editableColumns.reverse() : editableColumns,
          (col) =>
            backwards
              ? col.indexInRow < focusedColIndexInRow
              : col.indexInRow > focusedColIndexInRow,
        );
      }
    }

    if (
      lt(editableColIndex, 0) ||
      gt(editableColIndex, size(editableColumns) - 1)
    ) {
      // if the next editable column index is out of bounds, move to the next row recursively
      return this.findCellInTheNextRow({
        nextPosition,
        backwards,
        context,
      });
    }

    return NavigationUtils.createCellPosition(
      nextPosition.rowIndex,
      editableColumns[editableColIndex].column,
      nextPosition.rowPinned,
    );
  }

  /**
   * Get editable columns for a row (with caching)
   */
  private getEditableColumnsForRow(
    rowIndex: number,
    allColumns: Column[],
    gridApi: GridApi,
    options: IAgGridNavigationOptions,
  ): IEditableColumn[] {
    // Calculate editable columns
    const editableColumns = NavigationUtils.findEditableColumns(
      rowIndex,
      allColumns,
      gridApi,
      options,
    );

    return editableColumns;
  }

  private handleArrowSuppress(
    params: SuppressKeyboardEventParams,
    options: IAgGridNavigationOptions,
  ): boolean {
    const enableInputCursorBoundaryCheck =
      options.enableInputCursorBoundaryCheck ?? false;

    // If boundary check is not enabled, not suppress arrow key events by default to allow navigation
    if (!enableInputCursorBoundaryCheck) {
      params.event.preventDefault();

      return false;
    }

    const target = params.event.target as HTMLElement;
    const inputEl = target.closest('input') || target.querySelector('input');

    if (isNil(inputEl) || !(inputEl instanceof HTMLInputElement)) {
      return false;
    }

    const key = params.event.key;

    const cursorPosition = inputEl.selectionStart;
    const selectionEnd = inputEl.selectionEnd;
    const textLength = inputEl.value.length;

    const isAllContentSelected =
      isEqual(cursorPosition, 0) && isEqual(selectionEnd, textLength);

    // If all text is selected, allow grid navigation
    if (isAllContentSelected) {
      return false; // Allow ag-grid to handle navigation
    }

    // Allow input to handle navigation if cursor is not at boundary
    if (key === KEY_CODES.ARROW_LEFT && cursorPosition > 0) {
      return true; // Suppress ag-grid navigation, let input handle it
    }

    if (key === KEY_CODES.ARROW_RIGHT && cursorPosition < textLength) {
      return true; // Suppress ag-grid navigation, let input handle it
    }

    return false;
  }

  /**
   * Handle keyboard event suppression
   */
  private handleSuppressKeyboardEvent(
    params: SuppressKeyboardEventParams,
    options: IAgGridNavigationOptions,
  ): boolean {
    const key = params.event.key;

    // Only allow configured keys
    if (!options.allowedKeys.includes(key)) {
      return true; // Suppress the key
    }

    const specialHandleMapping = {
      [KEY_CODES.ENTER]: () => {
        params.event.preventDefault();
        queueMicrotask(() => {
          const inputEl = (params.event.target as HTMLElement).querySelector(
            'input',
          );
          if (inputEl) {
            const customEvent = new Event('gridCustomFocus', {
              bubbles: false,
            });
            inputEl.dispatchEvent(customEvent);
          }
        });

        return true;
      },
      [KEY_CODES.ARROW_LEFT]: this.handleArrowSuppress.bind(this),
      [KEY_CODES.ARROW_RIGHT]: this.handleArrowSuppress.bind(this),
    };

    const specialHandler = get(specialHandleMapping, key, false);

    if (isNil(specialHandler)) {
      return false;
    }

    return isFunction(specialHandler)
      ? specialHandler(params, options)
      : specialHandler;
  }

  private handleOnCellFocused(
    event: CellFocusedEvent,
    gridApi: GridApi,
    options: IAgGridNavigationOptions,
  ): void {
    if (!event || !event.column || isNil(event.rowIndex)) {
      return;
    }

    // Auto-focus editable cells
    if (
      NavigationUtils.isEditableCell(
        event.column,
        event.rowIndex,
        gridApi,
        options,
      )
    ) {
      const isSameAsFocusedGridContext =
        this.agGridService.isSameAsFocusedGridContext(event.api['context']);

      if (isSameAsFocusedGridContext || event.api?.getFocusedCell()) {
        // need this for inventory count page(async update)
        if (options.focusCellInnerElWithDebounce) {
          NavigationUtils.debounceFocusCellInnerElement(event);
        } else {
          NavigationUtils.focusCellInnerElement(event);
        }
      }

      if (isSameAsFocusedGridContext) {
        return;
      }

      const focusedGridApi =
        this.agGridService.focusedGridContext.getBean('gridApi');
      clearFocusedCellRenderer({ gridApi: focusedGridApi });
      focusedGridApi.clearFocusedCell();
      // update to new focused grid context after clear old focused cell to avoid potential conflict
      this.agGridService.updateFocusedGridContext(event.api['context']);
    }
  }

  private handleOnCellMouseDown(
    event: CellMouseDownEvent,
    gridApi: GridApi,
    options: IAgGridNavigationOptions,
  ): void {
    if (
      !event ||
      !event.column ||
      isNil(event.rowIndex) ||
      !NavigationUtils.isEditableCell(
        event.column,
        event.rowIndex,
        gridApi,
        options,
      )
    ) {
      return;
    }

    const nextCellElement = getCellRendererInstance({
      rowIndex: event.rowIndex,
      columnId: event.column.getColId(),
      gridApi,
    });

    const nextInputEl: HTMLElement = nextCellElement
      ?.getGui()
      ?.querySelector('input');

    if (isNil(nextInputEl)) {
      return;
    }

    queueMicrotask(() => {
      nextInputEl.focus();
    });
  }
}
